<template>
  <div class="flex flex-wrap col-12 border-box">
    <CustomerForm v-if="showCustomerForm" :customer="customer" :is-user="isUser" />
    <EmergencyContactForm v-else-if="showEmergencyContactForm" />
    <div v-else class="flex flex-wrap col-12 border-box white-bg body-padding-desktop">
      <div class="flex flex-column col-12 my2">
        <div class="flex col-12 items-center mb2">
          <span class="h6 col-11">Conformément à la réglementation, les mineurs ne peuvent pas détenir de compte digital.</span>
          <i class="col-1 icon icon-information-solid blue h3 center tooltip" @click="toggleToolTip">
            <span class="tooltiptext h5"> L'utilisateur de l'abonnement pourra accéder à son QRCode (donnant accès au centre) et réserver ses séances dans son espace personnel. Pour les mineurs : vous pourrez consulter les informations et réserver dans votre propre espace personnel. </span>
          </i>
        </div>
        <div v-for="(contact, i) in userContacts" :key="i" class="mt1 mb3">
          <div class="flex col-12 items-center" @click="selectParticipant(contact)">
            <input :checked="selectedParticipant.uuid === contact.uuid" type="radio" :name="contact.uuid" @input="selectParticipant(contact)">
            <span class="h4 bold">{{ contact.firstname }} {{ contact.lastname }}</span>
          </div>
          <div v-if="selectedParticipant.uuid === contact.uuid" class="flex flex-column ml3">
            <div v-if="showAgeError" class="flex flex-column col-12 my1">
              <span class="col-12 h6 red">L'âge de ce participant ne permet pas de bénéficier du tarif sélectionné. Veuillez sélectionner un autre participant ou un autre tarif à l'étape précédente.</span>
            </div>
            <span class="h6 blue underline mt2 mb3 pointer" :class="[{'red': !participantInfoFilled}]" @click="changeComponent('customer')"> Modifier ses coordonnées personnelles </span>
            <span class="h6">Contact d'urgence : {{ contactEmergencyFields?.firstname }} {{ contactEmergencyFields?.lastname }}</span>
            <span class="h6 blue underline pointer" :class="[{'red': !emergencyInfoFilled}]" @click="changeComponent('emergencyContact')"> Mettre à jour le contact d'urgence </span>
          </div>
        </div>
      </div>
      <div id="scrollToForm" class="flex flex-column col-12">
        <span class="h6 mb1">Si l'abonnement est pour une autre personne</span>
        <div :class="['flex col-12 p1 items-center pointer mb2', {'border-grey border-2': !showFormInPage}]" @click="toggle()">
          <span class="col-1 h3 center" style="padding-bottom: 3px;">+</span>
          <span class="col-10 h4 left-align">Ajouter un nouveau proche</span>
          <i v-if="showFormInPage" class="col-1 h2 icon icon-angle-up-icon icon-padding center"></i>
          <i v-else class="col-1 h2 icon icon-angle-down-icon icon-padding center"></i>
        </div>
      </div>
      <div v-if="showFormInPage" class="flex flex-wrap col-12">
        <span class="col-12 h6">Complétez les informations personnelles de votre proche : </span>
        <CustomerForm />
      </div>
    </div>
  </div>
</template>

<script>
import { differenceInYears, parse } from 'date-fns';
import CustomerForm from '@/components/subscription/CustomerForm.vue';
import EmergencyContactForm from '@/components/subscription/EmergencyContactForm.vue';
import { getRequiredFields } from '@/utils/utils';
import { computeDataLayerWithoutRoute } from '@/utils/index';

export default {
  components: { CustomerForm, EmergencyContactForm },
  props: {},
  data () {
    return {
      showFormInPage: false,
      customer: {},
      tooltip: false,
      isUser: false,
      showAgeError: false
    };
  },
  computed: {
    user () {
      return this.$store.state.user;
    },
    cartItem () {
      return this.$store.state.cartItem;
    },
    userContacts () {
      return this.user.contacts;
    },
    selectedParticipant () {
      return this.cartItem.participants[0] || {};
    },
    participantInfoFilled () {
      return this.getRequiredFields('editCustomer').every(field => !!this.selectedParticipant[field]);
    },
    contactEmergencyFields () {
      return this.selectedParticipant.emergency_contacts[0] || {};
    },
    emergencyInfoFilled () {
      return this.getRequiredFields('emergencyContact').every(field => !!this.contactEmergencyFields[field]);
    },
    showCustomerForm () {
      return this.$store.state.showCustomerForm;
    },
    showEmergencyContactForm () {
      return this.$store.state.showEmergencyContactForm;
    }
  },
  mounted () {
    this.$store.commit('setSubscriptionHeader', { title: 'Informations personnelles', subtitle: 'Qui va utiliser l\'abonnement ?', mobileSticky: true });
    if (!this.getRequiredFields('editCustomer').every(field => this.user[field])) {
      this.isUser = true;
      this.customer = this.user;
      this.$store.commit('setShowCustomerForm', true);
      this.$store.commit('setSubscriptionAction', 'editParticipant');
      return;
    }
    computeDataLayerWithoutRoute('selectParticipant');
  },
  methods: {
    getRequiredFields,
    scrollToForm () {
      const element = document.getElementById('scrollToForm');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
    toggle () {
      this.$store.commit('setCartItem', { ...this.cartItem, participants: [] });
      this.showFormInPage = !this.showFormInPage;
      this.$store.commit('setFormValid', false);
      this.$store.commit('setFormError', null);

      if (this.showFormInPage) {
        computeDataLayerWithoutRoute('createParticipant');
        this.$store.commit('setSubscriptionAction', 'createParticipant');
        this.$nextTick(() => {
          this.scrollToForm();
        });
      } else {
        computeDataLayerWithoutRoute('selectParticipant');
      }
    },
    selectParticipant (participant) {
      this.showAgeError = false;
      const participantAge = participant.birth_date ? differenceInYears(new Date(), parse(participant.birth_date, 'dd/MM/yyyy', new Date())) : null;
      let minTariffMaxAge = null;
      this.cartItem.tariffs.forEach(tarrif => {
        if (tarrif.tariffMaxAge && (minTariffMaxAge === null || tarrif.tariffMaxAge < minTariffMaxAge)) {
          minTariffMaxAge = tarrif.tariffMaxAge;
        }
      });
      if (!(!minTariffMaxAge || participantAge < minTariffMaxAge)) {
        this.showAgeError = true;
      }

      this.isUser = false;
      this.showFormInPage = false;
      this.$store.commit('setCartItem', { ...this.cartItem, participants: [participant] });
      this.$store.commit('setFormValid', this.emergencyInfoFilled && this.participantInfoFilled && (!minTariffMaxAge || participantAge < minTariffMaxAge));
      if (this.emergencyInfoFilled && this.participantInfoFilled && (!minTariffMaxAge || participantAge < minTariffMaxAge)) {
        this.$store.commit('setSubscriptionAction', 'recap');
      }
    },
    changeComponent (type) {
      if (type === 'customer') {
        this.customer = this.selectedParticipant;
        this.$store.commit('setShowCustomerForm', true);
        this.$store.commit('setSubscriptionAction', 'editParticipant');
        return;
      }
      this.customer = this.selectedParticipant;
      this.$store.commit('setShowEmergencyContactForm', true);
      this.$store.commit('setSubscriptionAction', 'editEmergencyContact');
      computeDataLayerWithoutRoute('editEmergencyContact');
    },
    toggleToolTip () {
      this.tooltip = !this.tooltip;
    }
  }
};
</script>

<style lang="scss" scoped>

.tooltip {
  position: relative;
  display: block;
}

.tooltip .tooltiptext {
  display: none;
  width: 20rem;
  background-color: var(--light-blue);
  color: #fff;
  text-align: center;
  padding: 1rem;
  border-radius: 6px;
  line-height: normal;
  position: absolute;
  z-index: 11;
  top: -7rem;
  right: 105%;
}

.tooltip:hover .tooltiptext {
  display: block;
}

.icon-padding {
  padding-top: 7px;
}

input[type="radio"]:checked::after{
  box-shadow: inset 0 0 0 2px var(--white);
  background-color: var(--strong-blue);
  border-color: var(--strong-blue)
}

input[type="radio"]::after{
  border: 1px solid var(--darkish-grey);
}
</style>
